import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const TABLE_HEADER_FILL = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FF2563EB' },
};
const TABLE_HEADER_FONT = {
  bold: true,
  color: { argb: 'FFFFFFFF' },
  size: 11,
};
const TOTAL_ROW_FILL = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FFEFF6FF' },
};
const TOTAL_ROW_FONT = {
  bold: true,
  color: { argb: 'FF1E3A8A' },
  size: 11,
};
const TITLE_FONT = {
  bold: true,
  color: { argb: 'FF1E3A8A' },
  size: 14,
};
const COMPANY_FONT = {
  bold: true,
  color: { argb: 'FF054D9E' },
  size: 16,
};
const INFO_FONT = {
  color: { argb: 'FF1E3A8A' },
  size: 10,
};

function parseLogoDataUrl(logoDataUrl) {
  if (!logoDataUrl) {
    return null;
  }
  const match = String(logoDataUrl).match(/^data:image\/(\w+);base64,(.+)$/);
  if (!match) {
    return null;
  }
  const extension = match[1].toLowerCase() === 'jpeg' || match[1].toLowerCase() === 'jpg'
    ? 'jpeg'
    : 'png';
  return { extension, base64: match[2] };
}

function getColumnAlignment(column) {
  if (column.align === 'end' || column.align === 'right') {
    return 'right';
  }
  if (column.align === 'center') {
    return 'center';
  }
  return 'left';
}

function applyBorder(cell) {
  cell.border = {
    top: { style: 'thin', color: { argb: 'FFBFDBFE' } },
    left: { style: 'thin', color: { argb: 'FFBFDBFE' } },
    bottom: { style: 'thin', color: { argb: 'FFBFDBFE' } },
    right: { style: 'thin', color: { argb: 'FFBFDBFE' } },
  };
}

function computeColumnTotals(columns, rows, totalKeys) {
  const totals = {};
  totalKeys.forEach((key) => {
    totals[key] = 0;
  });
  rows.forEach((row) => {
    totalKeys.forEach((key) => {
      if (!columns.some((column) => column.key === key)) {
        return;
      }
      const numericValue = Number(row[key]);
      if (Number.isFinite(numericValue)) {
        totals[key] += numericValue;
      }
    });
  });
  return totals;
}

function writeHeaderBlock(worksheet, workbook, {
  columnCount,
  companyName,
  addressLines,
  gstNo,
  logoDataUrl,
}) {
  const hasLogo = Boolean(parseLogoDataUrl(logoDataUrl));
  const textStartCol = hasLogo ? 2 : 1;
  const textEndCol = Math.max(columnCount, textStartCol + 2);

  if (hasLogo) {
    worksheet.getColumn(1).width = 14;
    worksheet.getRow(1).height = 24;
    worksheet.getRow(2).height = 24;
    worksheet.getRow(3).height = 24;
    worksheet.getRow(4).height = 24;
    const logo = parseLogoDataUrl(logoDataUrl);
    const imageId = workbook.addImage({
      base64: logo.base64,
      extension: logo.extension,
    });
    worksheet.addImage(imageId, {
      tl: { col: 0, row: 0 },
      ext: { width: 72, height: 72 },
    });
  }

  let headerRow = 1;
  if (companyName) {
    worksheet.mergeCells(headerRow, textStartCol, headerRow, textEndCol);
    const companyCell = worksheet.getCell(headerRow, textStartCol);
    companyCell.value = companyName;
    companyCell.font = COMPANY_FONT;
    companyCell.alignment = { vertical: 'middle', horizontal: 'left' };
    headerRow += 1;
  }

  (addressLines || []).forEach((line) => {
    worksheet.mergeCells(headerRow, textStartCol, headerRow, textEndCol);
    const addressCell = worksheet.getCell(headerRow, textStartCol);
    addressCell.value = line;
    addressCell.font = { size: 9 };
    addressCell.alignment = { vertical: 'middle', horizontal: 'left' };
    headerRow += 1;
  });

  if (gstNo) {
    worksheet.mergeCells(headerRow, textStartCol, headerRow, textEndCol);
    const gstCell = worksheet.getCell(headerRow, textStartCol);
    gstCell.value = `GST No.: ${gstNo}`;
    gstCell.font = { bold: true, size: 9 };
    gstCell.alignment = { vertical: 'middle', horizontal: 'left' };
    headerRow += 1;
  }

  return Math.max(headerRow, hasLogo ? 5 : headerRow);
}

export async function exportFormattedExcelReport({
  filename,
  documentTitle,
  infoLines = [],
  columns,
  rows,
  formatCellValue,
  totalKeys = ['quantity', 'amount'],
  header = {},
  workbook: externalWorkbook = null,
  worksheet: externalWorksheet = null,
}) {
  const workbook = externalWorkbook || new ExcelJS.Workbook();
  const worksheet = externalWorksheet || workbook.addWorksheet('Report');
  const columnCount = columns.length;
  let currentRow = writeHeaderBlock(worksheet, workbook, {
    columnCount,
    companyName: header.companyName || '',
    addressLines: header.addressLines || [],
    gstNo: header.gstNo || '',
    logoDataUrl: header.logoDataUrl || null,
  });

  currentRow += 1;
  worksheet.mergeCells(currentRow, 1, currentRow, columnCount);
  const titleCell = worksheet.getCell(currentRow, 1);
  titleCell.value = documentTitle;
  titleCell.font = TITLE_FONT;
  titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
  currentRow += 1;

  infoLines.filter(Boolean).forEach((line) => {
    worksheet.mergeCells(currentRow, 1, currentRow, columnCount);
    const infoCell = worksheet.getCell(currentRow, 1);
    infoCell.value = line;
    infoCell.font = INFO_FONT;
    infoCell.alignment = { horizontal: 'left', vertical: 'middle' };
    currentRow += 1;
  });

  currentRow += 1;
  const headerRowNumber = currentRow;
  columns.forEach((column, index) => {
    const cell = worksheet.getCell(headerRowNumber, index + 1);
    cell.value = column.label;
    cell.fill = TABLE_HEADER_FILL;
    cell.font = TABLE_HEADER_FONT;
    cell.alignment = {
      horizontal: getColumnAlignment(column),
      vertical: 'middle',
    };
    applyBorder(cell);
    worksheet.getColumn(index + 1).width = Math.max(column.label.length + 4, 12);
  });
  currentRow += 1;

  const formatValue = formatCellValue || ((value) => (value ?? ''));
  rows.forEach((row) => {
    columns.forEach((column, index) => {
      const cell = worksheet.getCell(currentRow, index + 1);
      const rawValue = row[column.key];
      const isNumericColumn = totalKeys.includes(column.key);
      if (isNumericColumn && Number.isFinite(Number(rawValue))) {
        cell.value = Number(rawValue);
        cell.numFmt = '#,##0.00';
      } else {
        cell.value = formatValue(rawValue, column.key);
      }
      cell.alignment = {
        horizontal: getColumnAlignment(column),
        vertical: 'middle',
      };
      applyBorder(cell);
    });
    currentRow += 1;
  });

  const totals = computeColumnTotals(columns, rows, totalKeys);
  const hasTotals = totalKeys.some((key) => columns.some((column) => column.key === key));
  if (hasTotals) {
    columns.forEach((column, index) => {
      const cell = worksheet.getCell(currentRow, index + 1);
      if (index === 0) {
        cell.value = 'Total';
      } else if (totalKeys.includes(column.key)) {
        cell.value = totals[column.key];
        cell.numFmt = '#,##0.00';
      } else {
        cell.value = '';
      }
      cell.fill = TOTAL_ROW_FILL;
      cell.font = TOTAL_ROW_FONT;
      cell.alignment = {
        horizontal: index === 0 ? 'left' : getColumnAlignment(column),
        vertical: 'middle',
      };
      applyBorder(cell);
    });
  }

  if (!externalWorkbook) {
    const buffer = await workbook.xlsx.writeBuffer();
    const safeFilename = filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`;
    saveAs(
      new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
      safeFilename,
    );
  }
}

const NUMERIC_KEY_PATTERN = /(_qty|_amt|amount|quantity|rate|balance|total|credit_limit|opening_balance)/i;

function inferTotalKeys(columns) {
  return columns
    .map((column) => column.key)
    .filter((key) => NUMERIC_KEY_PATTERN.test(key));
}

function normalizeTableExportColumns(columns, records) {
  const isReactTableColumnDefs = columns.length > 0
    && typeof columns[0] === 'object'
    && columns[0].headerName;

  if (isReactTableColumnDefs) {
    return {
      exportColumns: columns
        .filter((col) => col.headerName !== 'ROW_ACTIONS')
        .map((col) => ({
          key: col.fieldName,
          label: col.headerName,
          align: col.align === 'right' || col.align === 'end'
            ? 'right'
            : col.align === 'center'
              ? 'center'
              : 'left',
        })),
      exportRows: records,
    };
  }

  const isKeyLabelColumns = columns.length > 0
    && typeof columns[0] === 'object'
    && columns[0].key
    && columns[0].label;

  if (isKeyLabelColumns) {
    return {
      exportColumns: columns.map((column) => ({
        key: column.key,
        label: column.label,
        align: column.align === 'right' || column.align === 'end'
          ? 'right'
          : column.align === 'center'
            ? 'center'
            : 'left',
      })),
      exportRows: records,
    };
  }

  const headers = columns;
  const keys = records.length > 0 ? Object.keys(records[0]) : [];
  const exportColumns = headers.map((label, index) => ({
    key: keys[index] || `col_${index}`,
    label,
    align: 'left',
  }));
  const exportRows = records.map((record) => {
    if (Array.isArray(record)) {
      return Object.fromEntries(
        exportColumns.map((column, index) => [column.key, record[index] ?? '']),
      );
    }
    return record;
  });

  return { exportColumns, exportRows };
}

export async function exportFormattedTableExcel(columns, records, filename = 'export', options = {}) {
  const {
    documentTitle = filename,
    totalKeys,
    header = {},
    infoLines = [],
  } = options;
  const { exportColumns, exportRows } = normalizeTableExportColumns(columns, records);
  if (!exportColumns.length) {
    return;
  }

  const resolvedTotalKeys = totalKeys || inferTotalKeys(exportColumns);
  await exportFormattedExcelReport({
    filename,
    documentTitle,
    infoLines,
    columns: exportColumns,
    rows: exportRows,
    totalKeys: resolvedTotalKeys,
    header,
  });
}
